import {
  initVersionedSchema,
  toMentionContent,
  toParagraphContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IHasSourceIdentifier,
  type IPracticeMigration,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import { Interaction, toMention } from '@principle-theorem/principle-core';
import {
  CommunicationDirection,
  InteractionType,
  MentionResourceType,
  type IInteractionV2,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  getError,
  snapshotCombineLatest,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, upperFirst } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientInteractionDestinationEntity,
  type IBasePatientInteractionJobData,
  type IPatientInteractionMigrationData,
} from '../../../destination/entities/patient-interactions';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatientCommunication,
  PatientCommunicationSourceEntity,
} from '../../source/entities/patient-communication';
import { PatientNoteSourceEntity } from '../../source/entities/patient-notes';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { OasisPatientNoteCategoryToNoteMappingHandler } from '../mappings/patient-note-categories-to-notes';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';

interface IPatientInteractionJobData
  extends IBasePatientInteractionJobData<OasisPatient> {
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  noteCategories: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
}

export class PatientInteractionDestinationEntity extends BasePatientInteractionDestinationEntity<
  OasisPatient,
  IPatientInteractionJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientNote: new PatientNoteSourceEntity(),
    patientCommunication: new PatientCommunicationSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    patientNoteCategories: new OasisPatientNoteCategoryToNoteMappingHandler(),
    staff: new OasisStafferMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInteractionJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const noteCategories$ =
      this.customMappings.patientNoteCategories.getRecords(translationMap);

    return snapshotCombineLatest([staff$, noteCategories$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff, noteCategories]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          staff,
          noteCategories,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInteractionJobData
  ): Promise<
    | IPatientInteractionMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const sourcePatientNotes =
        await this.sourceEntities.patientNote.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const sourcePatientCommunications =
        await this.sourceEntities.patientCommunication.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const communicationInteractions =
        this._getPatientCommunicationInteractions(
          sourcePatientCommunications,
          await Firestore.getDoc(patientRef)
        );

      const interactions: (IInteractionV2 & IHasSourceIdentifier)[] = compact(
        sourcePatientNotes.map((note) => {
          const isClinicalNoteType = note.data.noteType === 0;
          if (isClinicalNoteType) {
            return;
          }

          const categoryId = note.data.categoryId;
          if (!categoryId) {
            return;
          }

          const noteCategoryMapping = data.noteCategories.find(
            (category) => category.sourceIdentifier === categoryId.toString()
          );

          if (!noteCategoryMapping?.destinationValue) {
            throw new Error(
              `Note category mapping not found for ${note.data.categoryId}`
            );
          }

          const isPatientNote = [
            ItemCodeNoteType.Omit,
            ItemCodeNoteType.ClinicalNote,
          ].includes(noteCategoryMapping.destinationValue.value);

          if (!isPatientNote) {
            return;
          }

          return {
            sourceIdentifier: note.record.uid,
            ...Interaction.init({
              type: InteractionType.Note,
              title: [
                toTextContent(
                  note.data.initials
                    ? `${note.data.initials} added a note`
                    : 'Added a note'
                ),
              ],
              createdAt: note.record.translations.createdAt,
              content: initVersionedSchema(note.data.note || undefined),
            }),
          };
        })
      );

      if (data.sourcePatient.data.healthWarnings) {
        interactions.push({
          sourceIdentifier: `${data.sourcePatient.record.uid}-healthWarning`,
          ...Interaction.init({
            type: InteractionType.Note,
            title: [toTextContent('Health warning')],
            content: initVersionedSchema(
              data.sourcePatient.data.healthWarnings
            ),
            pinned: true,
          }),
        });
      }

      if (data.sourcePatient.data.treatmentReminderNotes) {
        interactions.push({
          sourceIdentifier: `${data.sourcePatient.record.uid}-treatmentReminderNotes`,
          ...Interaction.init({
            type: InteractionType.Note,
            title: [toTextContent('Treatment Reminder Notes')],
            content: initVersionedSchema(
              data.sourcePatient.data.treatmentReminderNotes
            ),
            pinned: true,
          }),
        });
      }

      if (data.sourcePatient.data.financialWarnings) {
        interactions.push({
          sourceIdentifier: `${data.sourcePatient.record.uid}-financialWarnings`,
          ...Interaction.init({
            type: InteractionType.Note,
            title: [toTextContent('Treatment Reminder Notes')],
            content: initVersionedSchema(
              data.sourcePatient.data.financialWarnings
            ),
            pinned: true,
          }),
        });
      }

      return {
        patientRef,
        interactions: [...interactions, ...communicationInteractions],
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _getPatientCommunicationInteractions(
    sourcePatientCommunications: IGetRecordResponse<OasisPatientCommunication>[],
    patient: WithRef<IPatient>
  ): (IInteractionV2 & IHasSourceIdentifier)[] {
    return sourcePatientCommunications.map((communication) => {
      const interactionType = communication.data.type;
      const isResponse =
        communication.data.direction === CommunicationDirection.Inbound;
      const type = isResponse ? InteractionType.SmsReceived : interactionType;
      const title = isResponse
        ? [
            toMentionContent(toMention(patient, MentionResourceType.Patient)),
            toTextContent(` replied via ${upperFirst(interactionType)}`),
          ]
        : [
            toTextContent(`${upperFirst(interactionType)} sent to `),
            toMentionContent(toMention(patient, MentionResourceType.Patient)),
          ];
      return {
        sourceIdentifier: communication.record.uid,
        ...Interaction.init({
          type,
          title,
          createdAt: communication.record.translations.createdAt,
          content: initVersionedSchema(
            compact([
              communication.data.messageSubject
                ? toParagraphContent(communication.data.messageSubject)
                : undefined,
              communication.data.messageBody
                ? toParagraphContent(communication.data.messageBody)
                : undefined,
            ])
          ),
        }),
      };
    });
  }
}
